import { on, removeListener } from "api/prokey/prokey.websocket";
import React, { createContext, useEffect, useRef, useState } from "react"

const TaskContext = createContext()

const MAX_CACHED_SNIPPET_IMAGES = 8;

//-----------------------------------------------------------------
function snippetsEnabled() {
  //-----------------------------------------------------------------
  const value = localStorage.getItem( 'setting-snippets-enabled' );
  return ['1', 'true', 'yes', 'ja', 'on'].includes( value?.toLowerCase() );
}

//-----------------------------------------------------------------
function cacheSnippetImage(cache, id, data) {
  //-----------------------------------------------------------------
  cache.set(id, data);

  while( cache.size > MAX_CACHED_SNIPPET_IMAGES ) {
    const oldest_id = cache.keys().next().value;
    cache.delete(oldest_id);
  }
}

//-----------------------------------------------------------------
function TaskProvider( { children } ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const [ task      , setTask      ] = useState( {} );
  const [ documents , setDocuments ] = useState( {} );
  const [ blocks    , setBlocks    ] = useState( {} );
  const [ fields    , setFields    ] = useState( {} );

  const ref_images = useRef( new Map() );
  const [ images_revision, setImagesRevision ] = useState(0);

  const values = {
    images: ref_images.current, images_revision, clearImages,
  };

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleImage(data) {
      //-----------------------------------------------------------------
      if( !snippetsEnabled() ) {
        ref_images.current.clear();
        return;
      }

      const image_id = data?.id;
      if( !image_id ) return;

      const image_cache = ref_images.current;
      if( image_cache.has( image_id ) ) {
        const cached_image = image_cache.get( image_id );
        image_cache.delete( image_id );
        image_cache.set(image_id, { ...cached_image, ...data, html_image: cached_image.html_image });
        setImagesRevision(current => current + 1);
        return;
      }

      const temp = document.createElement('img');
      
      temp.onload = function() {
        if( image_cache !== ref_images.current || !snippetsEnabled() ) return;

        data.html_image = temp;
        cacheSnippetImage(image_cache, image_id, data);
        setImagesRevision(current => current + 1);
      };
      
      temp.onError = function() {
      }

      temp.src = data?.url;
    }

    function handleImageUpdate(data) {
      for (const image of data?.images?.update || []) {
        // Metadata refreshes must not decode every thumbnail as a full-size snippet.
        if (ref_images.current.has(image.id)) handleImage(image);
      }
    }

    on( 'IMAGE', handleImage );
    on( 'IMAGE_UPDATE', handleImageUpdate );

    return () => {
      removeListener( 'IMAGE' , handleImage );
      removeListener( 'IMAGE_UPDATE', handleImageUpdate );
    };
  }, [] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleTask(data) {
      //-----------------------------------------------------------------
      if( data?.id === task.id ) return;

      setTask( data );
      ref_images.current = new Map();
    }

    on( 'TASK_INFO', handleTask );

    return () => {
      removeListener( 'TASK_INFO' , handleTask );
    };
  }, [] )

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function clearImages() {
    //-----------------------------------------------------------------
    ref_images.current = new Map();
    setImagesRevision(current => current + 1);
  }

  //#endregion

  return (
    <TaskContext.Provider value={ values }>
      {children}
    </TaskContext.Provider>
  );
};

export { TaskContext, TaskProvider };
